/**
 * Modal component system
 *
 * Usage:
 *   <Modal onClose={handleClose}>
 *     <ModalBody>
 *       <ModalHeader>
 *         <ModalSubtitle>Create new</ModalSubtitle>
 *         <ModalTitle>Something Cool</ModalTitle>
 *       </ModalHeader>
 *
 *       << your form/content here >>
 *     </ModalBody>
 *   </Modal>
 *
 * Props:
 *   onClose        - called when close button clicked
 *   showClose      - show the X button (default: true)
 *   closePlacement - desktop X button placement: top-right or top-left
 *
 * Components:
 *   ModalBody     - scrollable content area with padding (use padding="none" to remove)
 *   ModalHeader   - container for title area, adds bottom margin
 *   ModalTitle    - the big text
 *   ModalSubtitle - the small text above the title
 */
import React, { createContext, forwardRef, useContext } from 'react';

import { cva } from 'class-variance-authority';

import { cn } from '../../lib/utils';

import SettingsXIcon from '../../../assets/images/settings-x.svg';

const ModalChromeContext = createContext({ showClose: true, closePlacement: 'top-right', padding: 'default' });

// NOTE: This is the *chrome* context (close-button placement + body padding) provided by <Modal>.
// It is intentionally distinct from useModalContext() in ModalPortal.jsx, which provides { unlocked, requestClose }.
// Kept under separate names so importing the wrong one fails loudly instead of silently returning the wrong shape.
export const useModalChrome = () => useContext(ModalChromeContext);

// These need to match the padding variants in ModalBody, so the button lines up with the content.
const closeButtonPositionStyles = {
  'top-right': {
    default: 'top-3 right-3',
    tiny: 'top-3 right-3',
    none: 'top-3 right-3',
  },
  'top-left': {
    default: 'top-5 left-4 md:left-6',
    tiny: 'top-3 left-3',
    none: 'top-3 left-3',
  },
};

const closeButtonVisibilityStyles = {
  all: 'flex',
  desktop: 'hidden md:flex',
  mobile: 'flex md:hidden',
};

const CloseButton = ({ onClick, padding = 'default', placement = 'top-right', visibility = 'desktop', className, icon }) => {
  const positionStyles = closeButtonPositionStyles[placement] || closeButtonPositionStyles['top-right'];
  const position = positionStyles[padding] || positionStyles.default;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'absolute z-(--stack-modal-close) size-7 cursor-pointer items-center justify-center rounded-full bg-gray-50 text-black hover:bg-gray-100',
        closeButtonVisibilityStyles[visibility] || closeButtonVisibilityStyles.desktop,
        position,
        className,
      )}
    >
      {icon ?? <SettingsXIcon className="pointer-events-none size-6" />}
    </button>
  );
};

const modalVariants = cva(
  'pointer-events-auto relative flex h-full w-full flex-col overflow-hidden rounded-3xl shadow-modal focus:outline-none',
  {
    variants: {
      variant: {
        default: 'border border-white bg-gray-200 dark:border-white/3',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Modal = forwardRef(
  (
    {
      children,
      className,
      variant,
      onClose,
      showClose = true,
      closePlacement = 'top-right',
      closeButtonClassName,
      closeIcon,
      padding = 'default',
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    // Normalize showClose to object format
    const closeConfig = typeof showClose === 'object' ? showClose : { desktop: showClose, mobile: false };

    const { desktop, mobile } = closeConfig;

    return (
      <ModalChromeContext.Provider value={{ showClose: Boolean(desktop || mobile), closePlacement, padding }}>
        <div className={cn('size-full', wrapperClassName)}>
          <div
            ref={ref}
            tabIndex={-1}
            className={cn(modalVariants({ variant }), className)}
            {...props}
          >
            {desktop && (
              <CloseButton
                onClick={onClose}
                padding={padding}
                placement={closePlacement}
                visibility={mobile === true ? 'all' : 'desktop'}
                className={closeButtonClassName}
                icon={closeIcon}
              />
            )}
            {!desktop && mobile === true && (
              <CloseButton
                onClick={onClose}
                padding={padding}
                placement={closePlacement}
                visibility="mobile"
                className={closeButtonClassName}
                icon={closeIcon}
              />
            )}
            {mobile && mobile !== true && <div className="md:hidden">{mobile}</div>}
            {children}
          </div>
        </div>
      </ModalChromeContext.Provider>
    );
  },
);
Modal.displayName = 'Modal';

export { Modal, modalVariants };
